import { observer } from 'mobx-react';
import React, { PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';

import useStores from 'hooks/use-stores';
import SnackBarProvider, { SnackBarContext } from 'pages/components/SnackBarProvider';

import HistoryOfChangesContext from './context';
import useHistoryOfChangesQuery from './use-history-of-changes-query';

interface Props {
    id: string;
}

const HistoryOfChangesProvider: React.FC<PropsWithChildren<Props>> = ({ id, children }) => {
    const snackBarRef = useRef<SnackBarContext>(null);
    const { userStore } = useStores();

    const [version, setVersion] = useState<number>(NaN);
    const { data, isLoading, isFetched, refetch } = useHistoryOfChangesQuery({ id }, { enabled: false });
    const loadingPromiseRef = useRef<Promise<void> | null>(null);
    const load = useCallback(async () => {
        if (isFetched) return;
        if (!userStore.userLoaded || !userStore.isAuthorized) return;
        if (loadingPromiseRef.current) return loadingPromiseRef.current;

        loadingPromiseRef.current = refetch()
            .then(([head]) => setVersion((version) => head?.version ?? version))
            .catch((error) =>
                snackBarRef.current?.openNotification({
                    level: 'error',
                    message: error.message || 'Something went wrong'
                })
            )
            .finally(() => {
                loadingPromiseRef.current = null;
            });

        return loadingPromiseRef.current;
    }, [isFetched, refetch, userStore.isAuthorized, userStore.userLoaded]);

    const revision = useMemo(() => data.find((item) => item.version === version) ?? null, [data, version]);

    const contextValue = useMemo(
        () => ({ data, revision, version, setVersion, isLoading, load }),
        [data, isLoading, load, revision, version]
    );

    return (
        <HistoryOfChangesContext.Provider value={contextValue}>
            <SnackBarProvider ref={snackBarRef}>{children}</SnackBarProvider>
        </HistoryOfChangesContext.Provider>
    );
};

export default observer(HistoryOfChangesProvider);
